import { Link } from 'react-router';
import { ROUTE_META } from '~/data/route-metadata';

interface RelatedPagesProps {
  path: string;  // Current route path
}

/**
 * Helper: Score a target route by tag overlap with current page.
 * Returns count of matching tags.
 */
function scoreTagOverlap(targetPath: string, currentPath: string): number {
  const currentMeta = ROUTE_META[currentPath];
  const targetMeta = ROUTE_META[targetPath];

  if (!currentMeta?.relatedTags || !targetMeta?.relatedTags) return 0;

  const currentTags = new Set(currentMeta.relatedTags);
  let overlap = 0;

  for (const tag of targetMeta.relatedTags) {
    if (currentTags.has(tag)) overlap++;
  }

  return overlap;
}

/**
 * Helper: Get category priority for tie-breaking related pages.
 * service > city > primary > optional
 */
function getCategoryPriority(category: string): number {
  const priorities: Record<string, number> = {
    service: 3,
    city: 2,
    primary: 1,
    optional: 0,
  };
  return priorities[category] ?? 0;
}

/**
 * Related Pages component.
 * Renders 3 related pages based on tag overlap.
 * Excludes: current page, /, /blog, /policies.
 */
export function RelatedPages({ path }: RelatedPagesProps) {
  const currentMeta = ROUTE_META[path];

  if (!currentMeta?.relatedTags) {
    return null;
  }

  // Build candidate list: all routes except current, /, /blog, /policies
  const candidates = Object.entries(ROUTE_META)
    .filter(([routePath]) => {
      return (
        routePath !== path &&
        routePath !== '/' &&
        routePath !== '/blog' &&
        routePath !== '/policies'
      );
    })
    .map(([routePath, meta]) => ({
      path: routePath,
      meta,
      score: scoreTagOverlap(routePath, path),
      categoryPriority: getCategoryPriority(meta.category),
    }))
    .filter(({ score }) => score > 0)  // Only include routes with at least 1 tag match
    .sort((a, b) => {
      // Sort by score descending, then by category priority descending
      if (a.score !== b.score) return b.score - a.score;
      return b.categoryPriority - a.categoryPriority;
    })
    .slice(0, 3);  // Top 3

  // If no good matches, return nothing
  if (candidates.length === 0) {
    return null;
  }

  return (
    <section className="related-pages" aria-label="Related services">
      <h3>You might also like</h3>
      <div className="related-pages-grid">
        {candidates.map(({ path: relPath, meta }) => (
          <Link
            key={relPath}
            to={relPath}
            className="related-card"
          >
            <strong>{meta.label}</strong>
            <span>{meta.description}</span>
            <span className="related-card-arrow">Learn more →</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
